/**
 * Resolves the submission shell menu for the active `<submission-detail>` instance.
 * Avoids `document.getElementById` when multiple `#sidebar-container` nodes could
 * exist (or when the wrong subtree was targeted); callers pass this host or any
 * descendant inside the same submission shell.
 */
function resolveSubmissionSidebar(scopeHost?: HTMLElement | null): HTMLElement | null {
  if (scopeHost) {
    const direct = scopeHost.querySelector('#sidebar-container');
    if (direct) {
      return direct as HTMLElement;
    }
    const shell = scopeHost.closest?.('submission-detail') ?? null;
    if (shell) {
      const scoped = shell.querySelector('#sidebar-container');
      if (scoped) {
        return scoped as HTMLElement;
      }
    }
  }
  const first = document.querySelector('submission-detail #sidebar-container');
  if (first) {
    return first as HTMLElement;
  }
  return document.getElementById('sidebar-container');
}

/**
 * Fully hides the submission left menu (`#sidebar-container`) so Add/Edit
 * (Data / PDF / … tab strip + content) uses the full row width; restores
 * the expanded menu when leaving Add/Edit.
 */
export function applySubmissionSidebarHidden(
  hidden: boolean,
  submissionDetailHost?: HTMLElement | null,
): void {
  const sidebar = resolveSubmissionSidebar(submissionDetailHost);
  if (!sidebar) return;
  const collapsibles = sidebar.getElementsByClassName('menu-collapsed');
  if (hidden) {
    sidebar.classList.add('sidebar-hidden');
    sidebar.classList.remove('sidebar-collapsed', 'sidebar-expanded');
  } else {
    sidebar.classList.remove('sidebar-hidden', 'sidebar-collapsed');
    sidebar.classList.add('sidebar-expanded');
    for (let i = 0; i < collapsibles.length; i++) {
      collapsibles[i].classList.remove('d-none');
    }
  }
}

/**
 * Sets `#sidebar-container` width (expanded 230px vs collapsed 60px + `.menu-collapsed`
 * labels hidden). The shell hamburger is decorative only; callers may still use this
 * for programmatic layout if needed.
 */
export function applySubmissionSidebarCollapsed(
  collapsed: boolean,
  submissionDetailHost?: HTMLElement | null,
): void {
  const sidebar = resolveSubmissionSidebar(submissionDetailHost);
  if (!sidebar) return;
  // Hamburger toggle is only meaningful when the shell is visible.
  sidebar.classList.remove('sidebar-hidden');
  const collapsibles = sidebar.getElementsByClassName('menu-collapsed');
  if (collapsed) {
    sidebar.classList.remove('sidebar-expanded');
    sidebar.classList.add('sidebar-collapsed');
    for (let i = 0; i < collapsibles.length; i++) {
      collapsibles[i].classList.add('d-none');
    }
  } else {
    sidebar.classList.remove('sidebar-collapsed');
    sidebar.classList.add('sidebar-expanded');
    for (let i = 0; i < collapsibles.length; i++) {
      collapsibles[i].classList.remove('d-none');
    }
  }
}
